import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  Stack,
  TextField,
  MenuItem,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  InputAdornment,
  useTheme,
  Checkbox,
  FormControlLabel,
  Divider,
  Fab,
  Drawer,
  CircularProgress,
  Tooltip as MuiTooltip,
  useMediaQuery,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  FileUpload as FileUploadIcon,
  Search as SearchIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Info as InfoIcon,
  Clear as ClearIcon,
  FilterList as FilterListIcon,
  Close as CloseIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Description as FileIcon,
  CreditCard as CreditCardIcon,
  Send as SendIcon,
  AutoAwesome as SparklesIcon,
} from "@mui/icons-material";
import Chart from "react-apexcharts";
import Papa from "papaparse";
import {
  parse,
  isValid,
  format,
  startOfToday,
  startOfMonth,
  endOfMonth,
  addMonths,
  addDays,
} from "date-fns";
import {
  getBrokerInsightsChat,
  getMessageUsage,
  incrementMessageUsage,
  DAILY_MESSAGE_LIMIT,
} from "./services/geminiService";
import { exportToCSV, generatePDFReport } from "./utils/rinnoviExports";

// --- HELPERS & UTILS ---

const formatCurrency = (value) => {
  return new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
  }).format(value || 0);
};

const formatPercent = (value) => {
  if (value === null || value === undefined) return "ND";
  return new Intl.NumberFormat("it-IT", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value / 100);
};

const parseCurrency = (value) => {
  if (!value) return 0;
  if (typeof value === "number") return value;
  let clean = String(value).trim();
  if (clean === "" || clean === "-" || clean === "ND") return 0;
  clean = clean.replace(/[€\s]/g, "");
  if (clean.includes(".") && clean.includes(",")) {
    if (clean.lastIndexOf(",") > clean.lastIndexOf(".")) {
      clean = clean.replace(/\./g, "").replace(",", ".");
    } else {
      clean = clean.replace(/,/g, "");
    }
  } else if (clean.includes(",")) {
    clean = clean.replace(",", ".");
  }
  const num = parseFloat(clean);
  return isNaN(num) ? 0 : num;
};

const parseDateString = (dateStr) => {
  if (!dateStr) return null;
  const clean = dateStr.trim();
  if (!clean || clean === "-" || clean === "ND") return null;
  const parsed = parse(clean, "dd/MM/yyyy", new Date());
  return isValid(parsed) ? parsed : null;
};

const calculatePriority = (record) => {
  let score = 0;
  switch (record.severitaAlert) {
    case "rosso_scuro":
      score += 80;
      break;
    case "rosso":
      score += 60;
      break;
    case "arancione":
      score += 40;
      break;
    case "giallo":
      score += 20;
      break;
    case "verde":
      score += 0;
      break;
    default:
      break;
  }
  if (!record.presenzaPreventivoRinnovo) score += 10;
  return Math.min(score, 100);
};

const determineAction = (record) => {
  if (record.severitaAlert === "rosso_scuro")
    return "Valutare alternative urgenti";
  if (record.severitaAlert === "rosso")
    return "Contattare cliente per spiegazione";
  if (record.severitaAlert === "arancione") return "Monitorare rinnovo";
  if (!record.presenzaPreventivoRinnovo) return "Sollecitare preventivo";
  return "Rinnovo standard";
};

const processRecords = (prodData, renewalData) => {
  const unified = [];
  const prodMap = new Map();
  prodData.forEach((p) => {
    if (p.Riferimento) prodMap.set(p.Riferimento.trim(), p);
  });

  renewalData.forEach((r, index) => {
    const ref = r.Riferimento ? r.Riferimento.trim() : "";
    const prod = prodMap.get(ref);

    const premioScadenza = parseCurrency(r["Premio lordo annuale in scadenza"]);
    const premioRinnovo = parseCurrency(r["Premio lordo annuale di rinnovo"]);
    const premioRataScadenza = parseCurrency(
      r["Premio lordo di rata in scadenza"],
    );
    const premioRataRinnovo = parseCurrency(
      r["Premio lordo di rata di rinnovo"],
    );

    const deltaAssoluto = premioRinnovo - premioScadenza;
    const deltaRataAssoluto = premioRataRinnovo - premioRataScadenza;

    let deltaPercent = null;
    if (premioScadenza > 0) {
      deltaPercent = ((premioRinnovo - premioScadenza) / premioScadenza) * 100;
    }

    let deltaRataPercent = null;
    if (
      premioRataScadenza > 0 &&
      (premioRataRinnovo > 0 ||
        String(r["Premio lordo di rata di rinnovo"]).trim() !== "")
    ) {
      deltaRataPercent =
        ((premioRataRinnovo - premioRataScadenza) / premioRataScadenza) * 100;
    }

    let frazionamento = r["Frazionamento polizza"] || "ND";
    if (frazionamento.toLowerCase().includes("annual"))
      frazionamento = "Annuale";
    else if (frazionamento.toLowerCase().includes("semestral"))
      frazionamento = "Semestrale";

    let causale = r.Causale || "ND";
    if (causale.toLowerCase().includes("scadenza"))
      causale = "Polizza in scadenza";
    else if (causale.toLowerCase().includes("rata"))
      causale = "Rata da saldare";

    let fascia = "Stabile +/- 5%";
    let severita = "verde";

    if (deltaPercent !== null) {
      if (deltaPercent < -5) {
        fascia = "Diminuzione > 5%";
        severita = "verde";
      } else if (deltaPercent >= -5 && deltaPercent <= 5) {
        fascia = "Stabile +/- 5%";
        severita = "verde";
      } else if (deltaPercent > 5 && deltaPercent <= 10) {
        fascia = "Aumento Lieve 5-10%";
        severita = "giallo";
      } else if (deltaPercent > 10 && deltaPercent <= 20) {
        fascia = "Aumento Medio 10-20%";
        severita = "arancione";
      } else if (deltaPercent > 20 && deltaPercent <= 30) {
        fascia = "Aumento Forte 20-30%";
        severita = "rosso";
      } else {
        fascia = "Aumento Critico > 30%";
        severita = "rosso_scuro";
      }
    }

    const hasPreventivo =
      !!r["Codice preventivo di rinnovo"] &&
      r["Codice preventivo di rinnovo"] !== "ND" &&
      r["Codice preventivo di rinnovo"] !== "-";

    const record = {
      ...r, // Keep original fields for export
      id: `rec-${index}`,
      collaboratoreRinnovo: r.Collaboratore || "ND",
      clienteRinnovo: r.Cliente || "ND",
      riferimento: ref,
      prodottoRinnovo: r.Prodotto || "ND",
      tariffa: prod?.Tariffa || "ND",
      scadenzaCopertura: parseDateString(r["Scadenza copertura"]),
      premioAnnualeScadenza: premioScadenza,
      premioAnnualeRinnovo: premioRinnovo,
      premioRataScadenza: premioRataScadenza,
      premioRataRinnovo: premioRataRinnovo,
      deltaRataAssoluto,
      deltaRataPercentuale: deltaRataPercent,
      deltaPercentualeCalcolato: deltaPercent,
      deltaAssolutoCalcolato: deltaAssoluto,
      fasciaAumento: fascia,
      severitaAlert: severita,
      presenzaPreventivoRinnovo: hasPreventivo,
      frazionamentoPolizza: frazionamento,
      causale: causale,
      isMatched: !!prod,
    };

    record.prioritaIntervento = calculatePriority(record);
    record.azioneConsigliata = determineAction(record);

    unified.push(record);
  });

  return unified;
};

const calculateKPIs = (prodCount, renewalCount, records) => {
  const matched = records.filter((r) => r.isMatched);
  const totalPremiumExpiring = records.reduce(
    (sum, r) => sum + r.premioAnnualeScadenza,
    0,
  );
  const totalPremiumRenewal = records.reduce(
    (sum, r) => sum + r.premioAnnualeRinnovo,
    0,
  );
  const totalDeltaAbsolute = records.reduce(
    (sum, r) => sum + r.deltaAssolutoCalcolato,
    0,
  );

  const validDeltas = records.filter(
    (r) => r.deltaPercentualeCalcolato !== null,
  );
  const avgDeltaPercent =
    validDeltas.length > 0
      ? validDeltas.reduce((sum, r) => sum + r.deltaPercentualeCalcolato, 0) /
        validDeltas.length
      : 0;

  const countIncrease20 = records.filter(
    (r) => (r.deltaPercentualeCalcolato || 0) > 20,
  ).length;
  const valueAtRiskHigh = records
    .filter((r) => (r.deltaPercentualeCalcolato || 0) > 20)
    .reduce((sum, r) => sum + r.premioAnnualeScadenza, 0);

  const totalInstallmentPremiumExpiring = records.reduce(
    (sum, r) => sum + r.premioRataScadenza,
    0,
  );
  const totalInstallmentPremiumRenewal = records.reduce(
    (sum, r) => sum + r.premioRataRinnovo,
    0,
  );
  const countInstallmentRenewalAvailable = records.filter(
    (r) => r.premioRataRinnovo > 0,
  ).length;
  const countInstallmentRenewalMissing = records.filter(
    (r) => r.premioRataRinnovo === 0,
  ).length;

  return {
    totalProduction: prodCount,
    totalRenewals: renewalCount,
    matchedCount: matched.length,
    matchPercentage:
      renewalCount > 0 ? (matched.length / renewalCount) * 100 : 0,
    totalPremiumExpiring,
    totalPremiumRenewal,
    totalDeltaAbsolute,
    avgDeltaPercent,
    countIncrease20,
    valueAtRiskHigh,
    totalInstallmentPremiumExpiring,
    totalInstallmentPremiumRenewal,
    avgInstallmentDeltaPercent: 0, // Placeholder
    countInstallmentRenewalAvailable,
    countInstallmentRenewalMissing,
  };
};

// --- COMPONENTS ---

const MultiSelectField = ({
  label,
  options,
  selected,
  onChange,
  placeholder,
}) => (
  <TextField
    select
    fullWidth
    label={label}
    SelectProps={{
      multiple: true,
      value: selected,
      onChange: (e) => onChange(e.target.value),
      renderValue: (selected) => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {selected.map((value) => (
            <Chip key={value} label={value} size="small" />
          ))}
        </Box>
      ),
    }}
    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
  >
    {options.map((name) => (
      <MenuItem key={name} value={name}>
        <Checkbox checked={selected.indexOf(name) > -1} />
        {name}
      </MenuItem>
    ))}
  </TextField>
);

const KPICard = ({ title, value, subValue, icon: Icon, color }) => (
  <Card
    sx={{
      height: "100%",
      borderRadius: "20px",
      boxShadow: "none",
      border: "1px solid #e2e8f0",
    }}
  >
    <CardContent
      sx={{
        p: 3,
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
      }}
    >
      <Box>
        <Typography
          variant="caption"
          sx={{
            color: "#64748b",
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            display: "block",
            mb: 1,
          }}
        >
          {title}
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 900, mb: 1 }}>
          {value}
        </Typography>
        {subValue && (
          <Typography
            variant="caption"
            sx={{ color: "#94a3b8", fontWeight: 500 }}
          >
            {subValue}
          </Typography>
        )}
      </Box>
      <Box
        sx={{
          p: 1.5,
          borderRadius: "12px",
          bgcolor: `${color}15`,
          color: color,
        }}
      >
        <Icon sx={{ fontSize: 24 }} />
      </Box>
    </CardContent>
  </Card>
);

const BrokerInsightPage = ({ currentUser }) => {
  const theme = useTheme();
  const [prodData, setProdData] = useState([]);
  const [renewalData, setRenewalData] = useState([]);
  const [unifiedData, setUnifiedData] = useState([]);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [pageSize, setPageSize] = useState(10);

  // --- PERSISTENT STORAGE (per-user) ---
  const storageKey = currentUser
    ? `meritx_rinnovi_${currentUser.username}`
    : null;

  // Load persisted data on mount / user change
  useEffect(() => {
    if (!storageKey) return;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const savedProd = parsed.prodData || [];
        const savedRenewal = parsed.renewalData || [];
        setProdData(savedProd);
        setRenewalData(savedRenewal);
        setUnifiedData(processRecords(savedProd, savedRenewal));
      } catch (e) {
        console.error("Errore nel ripristino dati Rinnovi", e);
      }
    }
  }, [storageKey]);

  // Auto-save whenever raw data changes
  useEffect(() => {
    if (!storageKey) return;
    if (prodData.length === 0 && renewalData.length === 0) return;
    localStorage.setItem(storageKey, JSON.stringify({ prodData, renewalData }));
  }, [prodData, renewalData, storageKey]);

  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    collaboratore: [],
    prodotto: [],
    tariffa: [],
    fasciaAumento: [],
    hasPreventivo: null,
    frazionamento: [],
    causale: [],
    showPremioRata: false,
    searchTerm: "",
  });

  const filteredData = useMemo(() => {
    return unifiedData.filter((record) => {
      if (
        filters.collaboratore.length > 0 &&
        !filters.collaboratore.includes(record.collaboratoreRinnovo)
      )
        return false;
      if (
        filters.prodotto.length > 0 &&
        !filters.prodotto.includes(record.prodottoRinnovo)
      )
        return false;
      if (
        filters.tariffa.length > 0 &&
        !filters.tariffa.includes(record.tariffa)
      )
        return false;
      if (
        filters.fasciaAumento.length > 0 &&
        !filters.fasciaAumento.includes(record.fasciaAumento)
      )
        return false;
      if (
        filters.frazionamento.length > 0 &&
        !filters.frazionamento.includes(record.frazionamentoPolizza)
      )
        return false;
      if (
        filters.causale.length > 0 &&
        !filters.causale.includes(record.causale)
      )
        return false;
      if (
        filters.hasPreventivo !== null &&
        record.presenzaPreventivoRinnovo !== filters.hasPreventivo
      )
        return false;

      if (
        filters.dateRange.start &&
        record.scadenzaCopertura &&
        record.scadenzaCopertura < filters.dateRange.start
      )
        return false;
      if (
        filters.dateRange.end &&
        record.scadenzaCopertura &&
        record.scadenzaCopertura > filters.dateRange.end
      )
        return false;

      if (filters.searchTerm) {
        const term = filters.searchTerm.toLowerCase();
        return (
          record.clienteRinnovo.toLowerCase().includes(term) ||
          record.riferimento.toLowerCase().includes(term)
        );
      }
      return true;
    });
  }, [unifiedData, filters]);

  const kpis = useMemo(
    () => calculateKPIs(prodData.length, renewalData.length, filteredData),
    [prodData.length, renewalData.length, filteredData],
  );

  const collaborators = useMemo(
    () =>
      Array.from(
        new Set(unifiedData.map((r) => r.collaboratoreRinnovo)),
      ).sort(),
    [unifiedData],
  );
  const products = useMemo(
    () => Array.from(new Set(unifiedData.map((r) => r.prodottoRinnovo))).sort(),
    [unifiedData],
  );
  const tariffs = useMemo(
    () => Array.from(new Set(unifiedData.map((r) => r.tariffa))).sort(),
    [unifiedData],
  );

  const setDateRangePreset = (preset) => {
    const today = startOfToday();
    let start = null,
      end = null;
    if (preset === "today") {
      start = today;
      end = today;
    } else if (preset === "thisMonth") {
      start = startOfMonth(today);
      end = endOfMonth(today);
    } else if (preset === "nextMonth") {
      const next = addMonths(today, 1);
      start = startOfMonth(next);
      end = endOfMonth(next);
    } else if (typeof preset === "number") {
      start = today;
      end = addDays(today, preset);
    }
    setFilters((prev) => ({ ...prev, dateRange: { start, end } }));
  };

  // --- CHARTS DATA PREP ---
  const distData = [
    {
      x: "Diminuzione",
      y: filteredData.filter((r) => r.fasciaAumento.includes("Diminuzione"))
        .length,
    },
    {
      x: "Stabile",
      y: filteredData.filter((r) => r.fasciaAumento.includes("Stabile")).length,
    },
    {
      x: "Lieve",
      y: filteredData.filter((r) => r.fasciaAumento.includes("Lieve")).length,
    },
    {
      x: "Medio",
      y: filteredData.filter((r) => r.fasciaAumento.includes("Medio")).length,
    },
    {
      x: "Forte",
      y: filteredData.filter((r) => r.fasciaAumento.includes("Forte")).length,
    },
    {
      x: "Critico",
      y: filteredData.filter((r) => r.fasciaAumento.includes("Critico")).length,
    },
  ];

  const prodAvgMap = new Map();
  filteredData.forEach((r) => {
    if (r.deltaPercentualeCalcolato !== null) {
      const curr = prodAvgMap.get(r.prodottoRinnovo) || { total: 0, count: 0 };
      prodAvgMap.set(r.prodottoRinnovo, {
        total: curr.total + r.deltaPercentualeCalcolato,
        count: curr.count + 1,
      });
    }
  });
  const prodChartData = Array.from(prodAvgMap.entries())
    .map(([k, v]) => ({ x: k, y: parseFloat((v.total / v.count).toFixed(2)) }))
    .sort((a, b) => b.y - a.y)
    .slice(0, 8);

  const pieCausale = Array.from(
    new Set(filteredData.map((r) => r.causale)),
  ).map((c) => ({
    label: c,
    value: filteredData.filter((r) => r.causale === c).length,
  }));

  const pieFraz = Array.from(
    new Set(filteredData.map((r) => r.frazionamentoPolizza)),
  ).map((c) => ({
    label: c,
    value: filteredData.filter((r) => r.frazionamentoPolizza === c).length,
  }));

  const scatterData = filteredData
    .filter((r) => r.deltaPercentualeCalcolato !== null)
    .map((r) => ({
      x: r.premioAnnualeScadenza,
      y: Math.round(r.deltaPercentualeCalcolato),
      z: r.premioAnnualeRinnovo,
      name: r.clienteRinnovo,
    }));

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ pb: 10 }}>
      {/* Header */}
      <Stack
        direction={isMobile ? "column" : "row"}
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 4 }}
        gap={2}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 900, letterSpacing: "-0.04em" }}
          >
            Rinnovi <span style={{ color: "#3b82f6" }}>Performance</span>
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#64748b", fontWeight: 500, mt: 0.5 }}
          >
            Engine analitico per il monitoraggio dei rinnovi e analisi delta
            premi
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          {unifiedData.length > 0 && (
            <>
              <Button
                variant="outlined"
                startIcon={<FileIcon />}
                onClick={() =>
                  exportToCSV(
                    filteredData,
                    `export_rinnovi_${
                      new Date().toISOString().split("T")[0]
                    }.csv`,
                  )
                }
                sx={{
                  borderRadius: "12px",
                  fontWeight: 800,
                  textTransform: "none",
                  borderColor: "#e2e8f0",
                }}
              >
                Esporta CSV
              </Button>
              <Button
                variant="outlined"
                startIcon={<FileIcon />}
                onClick={() => generatePDFReport(filteredData, kpis)}
                sx={{
                  borderRadius: "12px",
                  fontWeight: 800,
                  textTransform: "none",
                  borderColor: "#e2e8f0",
                }}
              >
                Esporta PDF
              </Button>
            </>
          )}
          <Button
            variant="contained"
            startIcon={<FileUploadIcon />}
            onClick={() => setIsUploadOpen(true)}
            sx={{
              borderRadius: "12px",
              fontWeight: 800,
              textTransform: "none",
              bgcolor: "#3b82f6",
              "&:hover": { bgcolor: "#2563eb" },
            }}
          >
            Carica Nuovi Dati
          </Button>
        </Stack>
      </Stack>

      {/* Main Dashboard Content */}
      {unifiedData.length === 0 ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: "column",
            py: 15,
            textAlign: "center",
            border: "2px dashed #e2e8f0",
            borderRadius: "40px",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyCenter: "center",
              mb: 3,
              boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
            }}
          >
            <FileIcon sx={{ fontSize: 40, color: "#cbd5e1" }} />
          </Box>
          <Typography
            variant="h5"
            sx={{
              fontWeight: 900,
              color: "#475569",
              mb: 1,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}
          >
            Nessun dato BrokerInsight caricato
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "#94a3b8", mb: 4, maxWidth: 400, mx: "auto", px: 4 }}
          >
            Carica i report di produzione e rinnovi per iniziare l'analisi
            automatica della tua agenzia.
          </Typography>
        </Box>
      ) : (
        <>
          {/* Alerts Row */}
          <Grid container spacing={2} sx={{ mb: 4 }}>
            {kpis.countIncrease20 > 0 && (
              <Grid item xs={12} md={4}>
                <Box
                  sx={{
                    p: 2,
                    bgcolor: "#fef2f2",
                    borderLeft: "4px solid #ef4444",
                    borderRadius: "4px 12px 12px 4px",
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <WarningIcon sx={{ color: "#ef4444" }} />
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 900,
                        color: "#991b1b",
                        textTransform: "uppercase",
                      }}
                    >
                      Aumenti Critici
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#b91c1c" }}>
                      Ci sono <b>{kpis.countIncrease20}</b> posizioni con
                      aumento superiore al 20%.
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            )}
            {kpis.countInstallmentRenewalMissing > 0 &&
              filters.showPremioRata && (
                <Grid item xs={12} md={4}>
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#fffbeb",
                      borderLeft: "4px solid #f59e0b",
                      borderRadius: "4px 12px 12px 4px",
                      display: "flex",
                      gap: 2,
                    }}
                  >
                    <WarningIcon sx={{ color: "#f59e0b" }} />
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{
                          fontWeight: 900,
                          color: "#92400e",
                          textTransform: "uppercase",
                        }}
                      >
                        Rate Mancanti
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#b45309" }}>
                        <b>{kpis.countInstallmentRenewalMissing}</b> posizioni
                        senza premio rata rinnovo.
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            <Grid item xs={12} md={4}>
              <Box
                sx={{
                  p: 2,
                  bgcolor: "#f0f9ff",
                  borderLeft: "4px solid #0ea5e9",
                  borderRadius: "4px 12px 12px 4px",
                  display: "flex",
                  gap: 2,
                  height: "100%",
                }}
              >
                <CheckCircleIcon sx={{ color: "#0ea5e9" }} />
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      fontWeight: 900,
                      color: "#075985",
                      textTransform: "uppercase",
                    }}
                  >
                    Analisi Completata
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#0369a1" }}>
                    Elaborati <b>{filteredData.length}</b> record unificati.
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Filter Bar */}
          <Card
            sx={{
              borderRadius: "20px",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              mb: 4,
            }}
          >
            <Box
              sx={{
                p: 2.5,
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                <FilterListIcon sx={{ color: "#3b82f6" }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    fontWeight: 900,
                    textTransform: "uppercase",
                    letterSpacing: "0.05em",
                  }}
                >
                  Filtri Dashboard
                </Typography>
              </Stack>
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={() =>
                  setFilters({
                    dateRange: { start: null, end: null },
                    collaboratore: [],
                    prodotto: [],
                    tariffa: [],
                    fasciaAumento: [],
                    hasPreventivo: null,
                    frazionamento: [],
                    causale: [],
                    showPremioRata: false,
                    searchTerm: "",
                  })
                }
                sx={{ fontWeight: 800, color: "#ef4444" }}
              >
                Resetta
              </Button>
            </Box>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    placeholder="Cerca cliente o targa..."
                    value={filters.searchTerm}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        searchTerm: e.target.value,
                      }))
                    }
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: "#94a3b8" }} />
                        </InputAdornment>
                      ),
                    }}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MultiSelectField
                    label="Collaboratore"
                    options={collaborators}
                    selected={filters.collaboratore}
                    onChange={(v) =>
                      setFilters((prev) => ({ ...prev, collaboratore: v }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MultiSelectField
                    label="Prodotto"
                    options={products}
                    selected={filters.prodotto}
                    onChange={(v) =>
                      setFilters((prev) => ({ ...prev, prodotto: v }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MultiSelectField
                    label="Fascia Aumento"
                    options={[
                      "Diminuzione > 5%",
                      "Stabile +/- 5%",
                      "Aumento Lieve 5-10%",
                      "Aumento Medio 10-20%",
                      "Aumento Forte 20-30%",
                      "Aumento Critico > 30%",
                    ]}
                    selected={filters.fasciaAumento}
                    onChange={(v) =>
                      setFilters((prev) => ({ ...prev, fasciaAumento: v }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <Stack direction="row" spacing={1}>
                    <TextField
                      type="date"
                      label="Da"
                      InputLabelProps={{ shrink: true }}
                      value={
                        filters.dateRange.start
                          ? format(filters.dateRange.start, "yyyy-MM-dd")
                          : ""
                      }
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          dateRange: {
                            ...prev.dateRange,
                            start: e.target.value
                              ? new Date(e.target.value)
                              : null,
                          },
                        }))
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                      }}
                    />
                    <TextField
                      type="date"
                      label="A"
                      InputLabelProps={{ shrink: true }}
                      value={
                        filters.dateRange.end
                          ? format(filters.dateRange.end, "yyyy-MM-dd")
                          : ""
                      }
                      onChange={(e) =>
                        setFilters((prev) => ({
                          ...prev,
                          dateRange: {
                            ...prev.dateRange,
                            end: e.target.value
                              ? new Date(e.target.value)
                              : null,
                          },
                        }))
                      }
                      sx={{
                        "& .MuiOutlinedInput-root": { borderRadius: "12px" },
                      }}
                    />
                  </Stack>
                </Grid>

                {/* MultiSelect Row 2 */}
                <Grid item xs={12} md={2}>
                  <MultiSelectField
                    label="Frazionamento"
                    options={["Annuale", "Semestrale", "Mensile"]}
                    selected={filters.frazionamento}
                    onChange={(v) =>
                      setFilters((prev) => ({ ...prev, frazionamento: v }))
                    }
                  />
                </Grid>
                <Grid item xs={12} md={2}>
                  <MultiSelectField
                    label="Causale"
                    options={["Polizza in scadenza", "Rata da saldare"]}
                    selected={filters.causale}
                    onChange={(v) =>
                      setFilters((prev) => ({ ...prev, causale: v }))
                    }
                  />
                </Grid>
                <Grid
                  item
                  xs={12}
                  md={2}
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={filters.showPremioRata}
                        onChange={(e) =>
                          setFilters((prev) => ({
                            ...prev,
                            showPremioRata: e.target.checked,
                          }))
                        }
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Mostra dati rata
                      </Typography>
                    }
                  />
                </Grid>

                <Grid item xs={12} sx={{ pt: 1 }}>
                  <Stack
                    direction={isMobile ? "column" : "row"}
                    spacing={4}
                    alignItems="left"
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 800,
                        color: "#94a3b8",
                        textTransform: "uppercase",
                        mr: 1,
                      }}
                    >
                      Periodi Rapidi:
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {[
                        "Oggi",
                        "7gg",
                        "30gg",
                        "Mese Corrente",
                        "Mese Prossimo",
                      ].map((p) => (
                        <Button
                          key={p}
                          size="small"
                          variant="outlined"
                          onClick={() => {
                            if (p === "Oggi") setDateRangePreset("today");
                            if (p === "7gg") setDateRangePreset(7);
                            if (p === "30gg") setDateRangePreset(30);
                            if (p === "Mese Corrente")
                              setDateRangePreset("thisMonth");
                            if (p === "Mese Prossimo")
                              setDateRangePreset("nextMonth");
                          }}
                          sx={{
                            borderRadius: "8px",
                            fontSize: "10px",
                            fontWeight: 900,
                            textTransform: "uppercase",
                            color: "#64748b",
                            borderColor: "#e2e8f0",
                          }}
                        >
                          {p}
                        </Button>
                      ))}
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Active Filter Chips */}
          <Box sx={{ mb: 4, display: "flex", flexWrap: "wrap", gap: 1 }}>
            {filters.searchTerm && (
              <Chip
                label={`Cerca: ${filters.searchTerm}`}
                onDelete={() =>
                  setFilters((prev) => ({ ...prev, searchTerm: "" }))
                }
                size="small"
              />
            )}
            {filters.collaboratore.map((v) => (
              <Chip
                key={v}
                label={`Collab: ${v}`}
                onDelete={() =>
                  setFilters((prev) => ({
                    ...prev,
                    collaboratore: prev.collaboratore.filter((x) => x !== v),
                  }))
                }
                size="small"
                color="primary"
                variant="outlined"
              />
            ))}
            {filters.prodotto.map((v) => (
              <Chip
                key={v}
                label={`Prod: ${v}`}
                onDelete={() =>
                  setFilters((prev) => ({
                    ...prev,
                    prodotto: prev.prodotto.filter((x) => x !== v),
                  }))
                }
                size="small"
                color="secondary"
                variant="outlined"
              />
            ))}
            {filters.dateRange.start && (
              <Chip
                label={`Dal: ${format(filters.dateRange.start, "dd/MM/yyyy")}`}
                onDelete={() =>
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: { ...prev.dateRange, start: null },
                  }))
                }
                size="small"
              />
            )}
            {filters.dateRange.end && (
              <Chip
                label={`Al: ${format(filters.dateRange.end, "dd/MM/yyyy")}`}
                onDelete={() =>
                  setFilters((prev) => ({
                    ...prev,
                    dateRange: { ...prev.dateRange, end: null },
                  }))
                }
                size="small"
              />
            )}
          </Box>

          {/* KPI GRID */}
          <Grid container spacing={3} sx={{ mb: 6 }}>
            <Grid item xs={12} md={3}>
              <KPICard
                title="Polizze"
                value={kpis.totalRenewals}
                subValue={`${kpis.matchedCount} abbinate (${Math.round(
                  kpis.matchPercentage,
                )}%)`}
                icon={FileIcon}
                color="#3b82f6"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <KPICard
                title="Premio Rinnovo"
                value={formatCurrency(kpis.totalPremiumRenewal)}
                subValue={`Delta: ${formatCurrency(kpis.totalDeltaAbsolute)}`}
                icon={MoneyIcon}
                color="#10b981"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <KPICard
                title="Aumento Medio"
                value={formatPercent(kpis.avgDeltaPercent)}
                subValue="Su record calcolabili"
                icon={TrendingUpIcon}
                color={kpis.avgDeltaPercent > 10 ? "#ef4444" : "#f59e0b"}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <KPICard
                title="Concentrazione Rischio"
                value={kpis.countIncrease20}
                subValue={`Valore: ${formatCurrency(kpis.valueAtRiskHigh)}`}
                icon={WarningIcon}
                color="#dc2626"
              />
            </Grid>

            {filters.showPremioRata && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ opacity: 0.5 }} />
                </Grid>
                <Grid item xs={12} md={3}>
                  <KPICard
                    title="Rate Scadenza"
                    value={formatCurrency(kpis.totalInstallmentPremiumExpiring)}
                    icon={CreditCardIcon}
                    color="#6366f1"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <KPICard
                    title="Rate Rinnovo"
                    value={formatCurrency(kpis.totalInstallmentPremiumRenewal)}
                    icon={CreditCardIcon}
                    color="#4f46e5"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <KPICard
                    title="Rate Disponibili"
                    value={kpis.countInstallmentRenewalAvailable}
                    icon={CheckCircleIcon}
                    color="#2dd4bf"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <KPICard
                    title="Rate Mancanti"
                    value={kpis.countInstallmentRenewalMissing}
                    icon={WarningIcon}
                    color="#fbbf24"
                  />
                </Grid>
              </>
            )}
          </Grid>

          {/* CHARTS GRID */}
          <Grid container spacing={4} sx={{ mb: 6 }}>
            <Grid item xs={12} md={6}>
              <ChartContainer title="Distribuzione Fasce Aumento">
                <Chart
                  options={{
                    chart: { id: "dist-bar", toolbar: { show: false } },
                    plotOptions: {
                      bar: { borderRadius: 6, distributed: true },
                    },
                    xaxis: {
                      categories: distData.map((d) => d.x),
                      labels: {
                        style: {
                          colors:
                            theme.palette.mode === "dark" ? "white" : "black",
                          fontWeight: 600,
                        },
                      },
                    },
                    yaxis: {
                      labels: {
                        style: {
                          colors:
                            theme.palette.mode === "dark" ? "white" : "black",
                          fontWeight: 600,
                        },
                      },
                    },
                    colors: [
                      "#10b981",
                      "#3b82f6",
                      "#facc15",
                      "#f97316",
                      "#ef4444",
                      "#7f1d1d",
                    ],
                    legend: { show: false },
                    tooltip: {
                      theme: theme.palette.mode === "dark" ? "dark" : "light",
                    },
                  }}
                  series={[{ name: "Volume", data: distData.map((d) => d.y) }]}
                  type="bar"
                  height={320}
                />
              </ChartContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartContainer title="Aumento Medio per Prodotto (%)">
                <Chart
                  options={{
                    chart: { id: "prod-bar", toolbar: { show: false } },
                    plotOptions: {
                      bar: {
                        borderRadius: 4,
                        horizontal: true,
                        dataLabels: { position: "top" },
                      },
                    },
                    dataLabels: {
                      enabled: true,
                      textAnchor: "start",
                      style: {
                        colors: [
                          theme.palette.mode === "dark" ? "white" : "black",
                        ],
                        fontWeight: 900,
                        fontSize: "12px",
                        fontFamily: "Inter, sans-serif",
                      },
                      formatter: (val) => `${val}%`,
                      offsetX: 10,
                      dropShadow: { enabled: false },
                    },
                    tooltip: {
                      theme: theme.palette.mode === "dark" ? "dark" : "light",
                      y: {
                        formatter: (val) => `${val}%`,
                      },
                    },
                    xaxis: {
                      categories: prodChartData.map((d) => d.x),
                      tickAmount: isMobile ? 4 : undefined,
                      labels: {
                        formatter: (v) => `${Math.round(v)}%`,
                        style: {
                          colors:
                            theme.palette.mode === "dark" ? "white" : "black",
                          fontWeight: 600,
                          fontSize: isMobile ? "10px" : "12px",
                        },
                      },
                    },
                    yaxis: {
                      labels: {
                        maxWidth: isMobile ? 90 : 160,
                        style: {
                          colors:
                            theme.palette.mode === "dark" ? "white" : "black",
                          fontWeight: 600,
                          fontSize: isMobile ? "10px" : "12px",
                        },
                        formatter: isMobile
                          ? (val) =>
                              val && val.length > 14
                                ? val.slice(0, 13) + "…"
                                : val
                          : (val) => val,
                      },
                    },
                    colors: ["#8b5cf6"],
                  }}
                  series={[
                    { name: "Aumento %", data: prodChartData.map((d) => d.y) },
                  ]}
                  type="bar"
                  height={isMobile ? 280 : 320}
                />
              </ChartContainer>
            </Grid>
            <Grid item xs={12} md={3}>
              <ChartContainer title="Frazionamento">
                <Chart
                  options={{
                    labels: pieFraz.map((d) => d.label),
                    colors: ["#3b82f6", "#10b981", "#f59e0b"],
                    legend: {
                      position: "bottom",
                      labels: {
                        colors:
                          theme.palette.mode === "dark" ? "white" : "black",
                      },
                    },
                    tooltip: {
                      theme: theme.palette.mode === "dark" ? "dark" : "light",
                    },
                  }}
                  series={pieFraz.map((d) => d.value)}
                  type="donut"
                  height={320}
                />
              </ChartContainer>
            </Grid>
            <Grid item xs={12} md={3}>
              <ChartContainer title="Causale">
                <Chart
                  options={{
                    labels: pieCausale.map((d) => d.label),
                    colors: ["#8b5cf6", "#0ea5e9"],
                    legend: {
                      position: "bottom",
                      labels: {
                        colors:
                          theme.palette.mode === "dark" ? "white" : "black",
                      },
                    },
                    tooltip: {
                      theme: theme.palette.mode === "dark" ? "dark" : "light",
                    },
                  }}
                  series={pieCausale.map((d) => d.value)}
                  type="donut"
                  height={320}
                />
              </ChartContainer>
            </Grid>
            <Grid item xs={12} md={6}>
              <ChartContainer title="Scatter: Premio vs Aumento (Dimensione = Rinnovo)">
                <Chart
                  options={{
                    chart: { id: "scatter-plot", toolbar: { show: true } },
                    xaxis: {
                      title: {
                        text: "Premio Scadenza (€)",
                        style: {
                          color:
                            theme.palette.mode === "dark" ? "white" : "black",
                        },
                      },
                      type: "numeric",
                      labels: {
                        style: {
                          colors:
                            theme.palette.mode === "dark" ? "white" : "black",
                          fontWeight: 600,
                        },
                      },
                    },
                    yaxis: {
                      title: {
                        text: "Aumento %",
                        style: {
                          color:
                            theme.palette.mode === "dark" ? "white" : "black",
                        },
                      },
                      labels: {
                        style: {
                          colors:
                            theme.palette.mode === "dark" ? "white" : "black",
                          fontWeight: 600,
                        },
                      },
                    },
                    legend: {
                      labels: {
                        colors:
                          theme.palette.mode === "dark" ? "white" : "black",
                      },
                    },
                    colors: ["#3b82f6"],
                    tooltip: {
                      theme: theme.palette.mode === "dark" ? "dark" : "light",
                    },
                  }}
                  series={[
                    {
                      name: "Polizze",
                      data: scatterData.map((d) => [d.x, d.y]),
                    },
                  ]}
                  type="scatter"
                  height={280}
                />
              </ChartContainer>
            </Grid>
          </Grid>

          {/* DATA TABLE */}
          <Card
            sx={{
              borderRadius: "24px",
              border: "1px solid #e2e8f0",
              boxShadow: "none",
              mb: 4,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                p: 3,
                borderBottom: "1px solid #f1f5f9",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 900 }}>
                Dettaglio Posizioni
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 700, color: "#94a3b8" }}
              >
                {filteredData.length} records visualizzati
              </Typography>
            </Box>
            <Box sx={{ height: 460, width: "100%" }}>
              <DataGrid
                rows={filteredData}
                columns={[
                  {
                    field: "clienteRinnovo",
                    headerName: "Cliente / Riferimento",
                    flex: 1.5,
                    renderCell: (params) => (
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 800 }}>
                          {params.row.clienteRinnovo}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: "#94a3b8",
                            display: "block",
                            fontStyle: "italic",
                            lineHeight: 1,
                          }}
                        >
                          {params.row.riferimento}
                        </Typography>
                      </Box>
                    ),
                  },
                  {
                    field: "prodottoRinnovo",
                    headerName: "Prodotto",
                    flex: 1,
                    renderCell: (params) => (
                      <Chip
                        label={params.value}
                        size="small"
                        sx={{
                          borderRadius: "6px",
                          fontWeight: 700,
                          fontSize: "10px",
                        }}
                      />
                    ),
                  },
                  {
                    field: "scadenzaCopertura",
                    headerName: "Scadenza",
                    width: 120,
                    valueFormatter: (params) =>
                      params.value ? format(params.value, "dd/MM/yyyy") : "-",
                  },
                  {
                    field: "premioAnnualeScadenza",
                    headerName: "Premio Scad.",
                    type: "number",
                    width: 130,
                    valueFormatter: (params) => formatCurrency(params.value),
                  },
                  {
                    field: "premioAnnualeRinnovo",
                    headerName: "Premio Rinn.",
                    type: "number",
                    width: 130,
                    renderCell: (params) => (
                      <Typography variant="body2" sx={{ fontWeight: 800 }}>
                        {formatCurrency(params.value)}
                      </Typography>
                    ),
                  },
                  ...(filters.showPremioRata
                    ? [
                        {
                          field: "premioRataScadenza",
                          headerName: "Rata Scad.",
                          type: "number",
                          width: 110,
                          valueFormatter: (params) =>
                            formatCurrency(params.value),
                        },
                        {
                          field: "premioRataRinnovo",
                          headerName: "Rata Rinn.",
                          type: "number",
                          width: 110,
                          renderCell: (params) =>
                            params.value === 0 ? (
                              <Chip label="N/D" size="small" color="warning" />
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{ fontWeight: 800 }}
                              >
                                {formatCurrency(params.value)}
                              </Typography>
                            ),
                        },
                        {
                          field: "deltaRataPercentuale",
                          headerName: "Delta Rata %",
                          type: "number",
                          width: 110,
                          renderCell: (params) => (
                            <Typography
                              variant="caption"
                              sx={{
                                color:
                                  (params.value || 0) > 0
                                    ? "#ef4444"
                                    : "#10b981",
                                fontWeight: 900,
                              }}
                            >
                              {params.value !== null
                                ? `${params.value > 0 ? "+" : ""}${Math.round(
                                    params.value,
                                  )}%`
                                : "-"}
                            </Typography>
                          ),
                        },
                      ]
                    : []),
                  {
                    field: "deltaPercentualeCalcolato",
                    headerName: "Delta %",
                    width: 100,
                    align: "center",
                    headerAlign: "center",
                    renderCell: (params) => (
                      <Box
                        sx={{
                          px: 1,
                          py: 0.5,
                          borderRadius: "6px",
                          bgcolor:
                            params.row.severitaAlert === "rosso_scuro"
                              ? "#7f1d1d"
                              : params.row.severitaAlert === "rosso"
                              ? "#fee2e2"
                              : params.row.severitaAlert === "arancione"
                              ? "#ffedd5"
                              : params.row.severitaAlert === "giallo"
                              ? "#fef9c3"
                              : "#f0fdf4",
                          color:
                            params.row.severitaAlert === "rosso_scuro"
                              ? "white"
                              : params.row.severitaAlert === "rosso"
                              ? "#b91c1c"
                              : params.row.severitaAlert === "arancione"
                              ? "#c2410c"
                              : params.row.severitaAlert === "giallo"
                              ? "#a16207"
                              : "#15803d",
                          display: "inline-block",
                          fontWeight: 900,
                          minWidth: 60,
                          textAlign: "center",
                        }}
                      >
                        {params.value !== null
                          ? `${params.value > 0 ? "+" : ""}${Math.round(
                              params.value,
                            )}%`
                          : "0%"}
                      </Box>
                    ),
                  },
                  {
                    field: "azioneConsigliata",
                    headerName: "Azione Consigliata",
                    flex: 1.2,
                    renderCell: (params) => (
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {params.value}
                      </Typography>
                    ),
                  },
                ]}
                pageSize={pageSize}
                onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                rowsPerPageOptions={[10, 25, 50]}
                disableSelectionOnClick
                density="compact"
                sx={{
                  border: "none",
                  "& .MuiDataGrid-columnHeaders": {
                    fontWeight: 800,
                  },
                  "& .MuiDataGrid-cell": {
                    borderBottom: "1px solid #6b6b6bff",
                  },
                }}
              />
            </Box>
          </Card>
        </>
      )}

      {/* Upload Dialog */}
      <UploadDialog
        open={isUploadOpen}
        onClose={() => setIsUploadOpen(false)}
        onUpload={(prod, renewal) => {
          const unified = processRecords(prod, renewal);
          setProdData(prod);
          setRenewalData(renewal);
          setUnifiedData(unified);
        }}
      />

      {/* AI CHATBOT */}
      <ChatbotBtn onClick={() => setIsChatOpen(!isChatOpen)} />
      <AIDrawer
        open={isChatOpen}
        onClose={() => setIsChatOpen(false)}
        kpis={kpis}
        data={filteredData}
      />
    </Box>
  );
};

const ChartContainer = ({ title, children }) => (
  <Card
    sx={{
      borderRadius: "24px",
      border: "1px solid #e2e8f0",
      boxShadow: "none",
      height: "100%",
    }}
  >
    <Box sx={{ p: 2.5, borderBottom: "1px solid #f1f5f9" }}>
      <Typography
        variant="subtitle2"
        sx={{
          fontWeight: 900,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {title}
      </Typography>
    </Box>
    <CardContent>{children}</CardContent>
  </Card>
);

const ChatbotBtn = ({ onClick }) => (
  <Fab
    color="primary"
    sx={{
      position: "fixed",
      bottom: 32,
      right: 100,
      zIndex: 1000,
      bgcolor: "#3b82f6",
      "&:hover": { bgcolor: "#2563eb" },
    }}
    onClick={onClick}
  >
    <SparklesIcon />
  </Fab>
);

const AIDrawer = ({ open, onClose, kpis, data }) => {
  const [messages, setMessages] = useState([
    {
      role: "ai",
      text: "Ciao! Sono il tuo assistente BrokerInsight. Come posso aiutarti oggi?",
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [usedCount, setUsedCount] = useState(() => getMessageUsage().count);
  const endRef = useRef(null);

  // Sync usage count whenever the drawer opens
  useEffect(() => {
    if (open) setUsedCount(getMessageUsage().count);
  }, [open]);

  useEffect(
    () => endRef.current?.scrollIntoView({ behavior: "smooth" }),
    [messages],
  );

  const remaining = DAILY_MESSAGE_LIMIT - usedCount;
  const limitReached = remaining <= 0;
  const usagePercent = Math.min((usedCount / DAILY_MESSAGE_LIMIT) * 100, 100);
  const usageColor =
    remaining > 10 ? "#10b981" : remaining > 5 ? "#f59e0b" : "#ef4444";

  const handleSend = async () => {
    if (!input.trim() || limitReached) return;
    const userMsg = input;
    setMessages((prev) => [...prev, { role: "user", text: userMsg }]);
    setInput("");
    setIsTyping(true);

    // Increment usage counter
    const updated = incrementMessageUsage();
    setUsedCount(updated.count);

    try {
      const response = await getBrokerInsightsChat(
        kpis,
        data
          .filter(
            (r) =>
              r.severitaAlert === "rosso" || r.severitaAlert === "rosso_scuro",
          )
          .slice(0, 10),
        userMsg,
      );
      setMessages((prev) => [...prev, { role: "ai", text: response }]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: "ai", text: "Errore durante l'elaborazione dell'insight." },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 450,
          borderRadius: "24px 0 0 24px",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          pb: 2,
          bgcolor: "#3b82f6",
          color: "white",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1.5,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <SparklesIcon />
            <Typography variant="h6" sx={{ fontWeight: 900 }}>
              AI Broker Advisor
            </Typography>
          </Stack>
          <IconButton onClick={onClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Usage indicator */}
        <Box
          sx={{
            bgcolor: "rgba(255,255,255,0.15)",
            borderRadius: "12px",
            p: 1.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 0.75,
            }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, opacity: 0.9 }}
            >
              Messaggi oggi
            </Typography>
            <Typography
              variant="caption"
              sx={{
                fontWeight: 900,
                bgcolor: limitReached ? "#ef4444" : "rgba(255,255,255,0.25)",
                px: 1,
                py: 0.25,
                borderRadius: "6px",
              }}
            >
              {limitReached ? "Limite raggiunto" : `${remaining} rimanenti`}
            </Typography>
          </Box>
          {/* Progress bar */}
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.2)",
              borderRadius: "4px",
              height: 6,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                height: "100%",
                width: `${usagePercent}%`,
                bgcolor: usageColor,
                borderRadius: "4px",
                transition: "width 0.4s ease, background-color 0.4s ease",
              }}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{ opacity: 0.75, mt: 0.5, display: "block" }}
          >
            {usedCount} / {DAILY_MESSAGE_LIMIT} — si resetta ogni giorno
          </Typography>
        </Box>
      </Box>

      {/* Messages */}
      <Box sx={{ flex: 1, p: 3, overflowY: "auto", bgcolor: "#f8fafc" }}>
        <Stack spacing={2}>
          {messages.map((m, i) => (
            <Box
              key={i}
              sx={{
                alignSelf: m.role === "ai" ? "flex-start" : "flex-end",
                maxWidth: "85%",
              }}
            >
              <Paper
                sx={{
                  p: 2,
                  borderRadius:
                    m.role === "ai"
                      ? "2px 16px 16px 16px"
                      : "16px 16px 2px 16px",
                  bgcolor: m.role === "ai" ? "blue.100" : "#3b82f6",
                  color: m.role === "ai" ? "text.primary" : "white",
                  boxShadow: "0 2px 10px rgba(0,0,0,0.05)",
                }}
              >
                <Typography variant="body2">{m.text}</Typography>
              </Paper>
            </Box>
          ))}
          {isTyping && <CircularProgress size={20} sx={{ ml: 2 }} />}
          {limitReached && (
            <Box
              sx={{
                p: 2,
                bgcolor: "#fef2f2",
                borderRadius: "12px",
                border: "1px solid #fecaca",
                textAlign: "center",
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: "#b91c1c", fontWeight: 700 }}
              >
                🚫 Hai raggiunto il limite giornaliero di {DAILY_MESSAGE_LIMIT}{" "}
                messaggi.
              </Typography>
              <Typography variant="caption" sx={{ color: "#ef4444" }}>
                Il contatore si azzera automaticamente domani.
              </Typography>
            </Box>
          )}
          <div ref={endRef} />
        </Stack>
      </Box>

      {/* Input */}
      <Box sx={{ p: 3, borderTop: "1px solid #e2e8f0" }}>
        <MuiTooltip
          title={
            limitReached
              ? `Limite di ${DAILY_MESSAGE_LIMIT} messaggi/giorno raggiunto. Riprova domani.`
              : ""
          }
          placement="top"
        >
          <span style={{ display: "block" }}>
            <TextField
              fullWidth
              disabled={limitReached}
              placeholder={
                limitReached
                  ? "Limite giornaliero raggiunto"
                  : "Chiedimi della performance..."
              }
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSend()}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleSend}
                      color="primary"
                      disabled={limitReached || !input.trim()}
                    >
                      <SendIcon />
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { borderRadius: "12px" },
              }}
            />
          </span>
        </MuiTooltip>
      </Box>
    </Drawer>
  );
};

const UploadDialog = ({ open, onClose, onUpload }) => {
  const [prodFile, setProdFile] = useState(null);
  const [renewalFile, setRenewalFile] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleUpload = async () => {
    if (!prodFile || !renewalFile) return;
    setLoading(true);

    const parseFile = (file) => {
      return new Promise((resolve) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => resolve(results.data),
        });
      });
    };

    const prodData = await parseFile(prodFile);
    const renewalData = await parseFile(renewalFile);
    onUpload(prodData, renewalData);
    setLoading(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: "24px", p: 2 } }}
    >
      <DialogTitle sx={{ fontWeight: 900, textAlign: "center" }}>
        Caricamento Report Bi-Annuale
      </DialogTitle>
      <DialogContent>
        <Typography
          variant="body2"
          sx={{ color: "#64748b", textAlign: "center", mb: 4 }}
        >
          Carica i file CSV della produzione dell'anno precedente e dei rinnovi
          dell'anno in corso per generare il matching.
        </Typography>
        <Stack spacing={3}>
          <Box
            sx={{
              p: 4,
              border: "2px dashed #e2e8f0",
              borderRadius: "16px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#3b82f6" },
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              onChange={(e) => setProdFile(e.target.files[0])}
              accept=".csv"
            />
            <FileUploadIcon sx={{ color: "#3b82f6", mb: 1, fontSize: 32 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {prodFile ? prodFile.name : "Report Produzione (Precedente)"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Clicca per selezionare il file CSV
            </Typography>
          </Box>
          <Box
            sx={{
              p: 4,
              border: "2px dashed #e2e8f0",
              borderRadius: "16px",
              textAlign: "center",
              cursor: "pointer",
              transition: "all 0.2s",
              "&:hover": { borderColor: "#3b82f6" },
            }}
            component="label"
          >
            <input
              type="file"
              hidden
              onChange={(e) => setRenewalFile(e.target.files[0])}
              accept=".csv"
            />
            <FileUploadIcon sx={{ color: "#3b82f6", mb: 1, fontSize: 32 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
              {renewalFile ? renewalFile.name : "Report Rinnovi (Attuale)"}
            </Typography>
            <Typography variant="caption" sx={{ color: "#94a3b8" }}>
              Clicca per selezionare il file CSV
            </Typography>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ p: 3, justifyContent: "center", gap: 2 }}>
        <Button onClick={onClose} sx={{ fontWeight: 800, color: "#64748b" }}>
          Annulla
        </Button>
        <Button
          variant="contained"
          disabled={!prodFile || !renewalFile || loading}
          onClick={handleUpload}
          sx={{
            borderRadius: "12px",
            px: 4,
            py: 1,
            fontWeight: 800,
            bgcolor: "#3b82f6",
          }}
        >
          {loading ? "Elaborazione..." : "Esegui Matching"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BrokerInsightPage;
